import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useStore } from "@/store/useStore";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Trophy, 
  Users, 
  Clock, 
  Coins,
  Zap,
  Bot,
  Loader2,
  Crown,
  AlertTriangle,
  RefreshCw,
  Timer,
  Star,
  ChevronRight
} from "lucide-react";

import { API_BASE as API_URL } from "@/config/api";

export default function TournamentsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, token, fetchTournaments } = useStore();
  
  const [autoTournaments, setAutoTournaments] = useState([]);
  const [regularTournaments, setRegularTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(null);
  const [activeTab, setActiveTab] = useState("arena");

  const loadTournaments = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch 24/7 auto tournaments
      const autoRes = await fetch(`${API_URL}/tournaments/auto/available`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (autoRes.ok) {
        const autoData = await autoRes.json();
        setAutoTournaments(autoData.tournaments || []);
      }
      
      // Fetch regular tournaments
      const regularData = await fetchTournaments();
      setRegularTournaments(regularData?.filter(t => !t.is_auto) || []);
    } catch (err) {
      console.error("Failed to load tournaments:", err);
      toast.error("Не удалось загрузить турниры");
    } finally {
      setLoading(false);
    }
  }, [token, fetchTournaments]);

  useEffect(() => {
    loadTournaments();
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadTournaments, 30000);
    return () => clearInterval(interval);
  }, [loadTournaments]);

  const handleJoinAuto = async (tournamentId) => {
    if (!user) {
      toast.error("Войдите в аккаунт");
      navigate("/login");
      return;
    }

    const tournament = autoTournaments.find(t => t.id === tournamentId);
    
    if (tournament?.entry_fee > 0) {
      if (tournament.entry_fee > (user?.balance || 0)) {
        toast.error(t("wallet.insufficientBalance"));
        return;
      }
    }

    setJoining(tournamentId);
    try {
      const res = await fetch(`${API_URL}/tournaments/auto/${tournamentId}/join`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });
      
      let data;
      try {
        data = await res.json();
      } catch {
        data = {};
      }
      
      if (res.ok) {
        toast.success(t("tournament.joined"), {
          description: data.note
        });
        loadTournaments();
        // Navigate to tournament detail
        navigate(`/tournaments/${tournamentId}`);
      } else {
        toast.error(data.detail || t("tournament.joinError"));
      }
    } catch (err) {
      console.error("Join tournament error:", err);
      toast.error(t("tournament.connectionError"));
    } finally {
      setJoining(null);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (secs === 0) return `${mins} ${t("tournament.minutes")}`;
    return `${mins}+${secs}`;
  };

  const getTimeUntilStart = (startTime) => {
    const start = new Date(startTime);
    const now = new Date();
    const diff = start - now;
    
    if (diff <= 0) return t("tournament.started");
    
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return t("tournament.inMinutes", { count: mins });
    
    const hours = Math.floor(mins / 60);
    return t("tournament.inHours", { count: hours });
  };

  // Auto Tournament Card Component
  const AutoTournamentCard = ({ tournament }) => {
    const isFree = tournament.entry_fee === 0;
    const hasStarted = tournament.status === "active";
    
    return (
      <Card 
        className="tournament-card glass border-border/50 overflow-hidden transition-all duration-300 hover:border-primary/50 hover:shadow-lg hover:shadow-primary/10"
        data-testid={`auto-tournament-${tournament.id}`}
      >
        {/* Header with gradient */}
        <div className={`p-4 ${isFree ? 'bg-gradient-to-r from-emerald-600/20 to-green-600/20' : 'bg-gradient-to-r from-amber-600/20 to-yellow-600/20'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-2xl">{tournament.icon || "🏆"}</span>
              <div>
                <h3 className="font-bold text-lg">{tournament.name}</h3>
                <p className="text-sm text-muted-foreground">{tournament.description}</p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Badge variant={hasStarted ? "default" : "secondary"} className="animate-pulse">
                {hasStarted ? t("tournament.live") : t("tournament.registration")}
              </Badge>
              <Badge variant="outline" className="text-xs">
                <Bot className="h-3 w-3 mr-1" />
                24/7
              </Badge>
            </div>
          </div>
        </div>
        
        <CardContent className="p-4 space-y-4">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
              <Users className="h-4 w-4 text-primary" />
              <div>
                <p className="text-xs text-muted-foreground">{t("tournament.players")}</p>
                <p className="font-semibold">{tournament.current_players}/{tournament.max_players}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
              <Clock className="h-4 w-4 text-primary" />
              <div>
                <p className="text-xs text-muted-foreground">{t("tournament.timeControl")}</p>
                <p className="font-semibold">{formatTime(tournament.time_control)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
              <Timer className="h-4 w-4 text-primary" />
              <div>
                <p className="text-xs text-muted-foreground">{t("tournament.start")}</p>
                <p className="font-semibold">{getTimeUntilStart(tournament.start_time)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
              <Zap className="h-4 w-4 text-primary" />
              <div>
                <p className="text-xs text-muted-foreground">{t("tournament.format")}</p>
                <p className="font-semibold">Arena</p>
              </div>
            </div>
          </div>
          
          {/* Prize & Entry */}
          <div className="flex items-center justify-between pt-3 border-t border-border/50">
            <div>
              <p className="text-xs text-muted-foreground">{t("tournament.entryFee")}</p>
              <p className={`font-bold text-lg ${isFree ? 'text-green-500' : 'text-primary'}`}>
                {isFree ? t("tournament.free") : `${tournament.entry_fee} Points`}
              </p>
            </div>
            {tournament.prize_pool > 0 && (
              <div className="text-right">
                <p className="text-xs text-muted-foreground">{t("tournament.prizePool")}</p>
                <p className="font-bold text-lg text-primary">
                  {tournament.prize_pool} Points
                </p>
              </div>
            )}
          </div>
          
          {/* Bot Notice */}
          <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
            <Bot className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-blue-300">
              {t("tournament.botNotice")}
            </p>
          </div>
          
          {/* Join Button */}
          <Button 
            className={`w-full ${isFree ? 'bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500' : 'btn-gold'}`}
            onClick={() => tournament.is_registered ? navigate(`/tournaments/${tournament.id}`) : handleJoinAuto(tournament.id)}
            disabled={joining === tournament.id || (!tournament.is_registered && tournament.current_players >= tournament.max_players)}
            data-testid={tournament.is_registered ? `open-auto-${tournament.id}` : `join-auto-${tournament.id}`}
          >
            {joining === tournament.id ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("tournament.joining")}
              </>
            ) : !tournament.is_registered && tournament.current_players >= tournament.max_players ? (
              t("tournament.full")
            ) : (
              <>
                <Crown className="mr-2 h-4 w-4" />
                {tournament.is_registered ? t("tournament.registered") : t("tournament.join")}
                <ChevronRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    );
  };

  // Regular Tournament Card
  const RegularTournamentCard = ({ tournament }) => (
    <Card 
      className="tournament-card glass border-border/50 card-interactive cursor-pointer hover:border-primary/50 transition-colors"
      data-testid={`tournament-${tournament.id}`}
      onClick={() => navigate(`/tournaments/${tournament.id}`)}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <CardTitle className="text-lg">{tournament.name}</CardTitle>
            {tournament.description && (
              <p className="text-sm text-muted-foreground">{tournament.description}</p>
            )}
          </div>
          <Badge variant={tournament.format === "swiss" ? "default" : "secondary"}>
            {tournament.format === "swiss" ? "Swiss" : "Arena"}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Users className="h-4 w-4" />
            <span>{tournament.current_players}/{tournament.max_players}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>{formatTime(tournament.time_control)}</span>
          </div>
        </div>
        
        <div className="flex items-center justify-between pt-2 border-t border-border/50">
          <div>
            <p className="text-xs text-muted-foreground">Взнос</p>
            <p className="font-bold text-primary">
              {tournament.entry_fee > 0 ? `${tournament.entry_fee} P` : "Бесплатно"}
            </p>
          </div>
          {tournament.prize_pool > 0 && (
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Призы</p>
              <p className="font-bold text-primary">{tournament.prize_pool} P</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="tournaments-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight flex items-center gap-3">
            <Trophy className="h-8 w-8 text-primary" />
            {t("tournament.title")}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t("tournament.subtitle")}
          </p>
        </div>
        <Button 
          variant="outline" 
          size="icon"
          onClick={loadTournaments}
          disabled={loading}
          data-testid="refresh-tournaments"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Info Banner */}
      <Card className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border-primary/20">
        <CardContent className="p-4 flex items-center gap-4">
          <div className="p-3 rounded-full bg-primary/20">
            <Zap className="h-6 w-6 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold">{t("tournament.botTitle")}</h3>
            <p className="text-sm text-muted-foreground">
              {t("tournament.botInfo")}
            </p>
          </div>
          <Badge variant="outline" className="hidden sm:flex">
            <Bot className="h-3 w-3 mr-1" />
            AI Opponents
          </Badge>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="arena" data-testid="tab-arena" className="gap-2">
            <Zap className="h-4 w-4" />
            {t("tournament.arena")} 24/7
          </TabsTrigger>
          <TabsTrigger value="regular" data-testid="tab-regular" className="gap-2">
            <Trophy className="h-4 w-4" />
            {t("tournament.swiss")}
          </TabsTrigger>
        </TabsList>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Загрузка турниров...</p>
          </div>
        ) : (
          <>
            {/* 24/7 Arena Tournaments */}
            <TabsContent value="arena" className="mt-6">
              {autoTournaments.length === 0 ? (
                <Card className="glass border-border/50">
                  <CardContent className="flex flex-col items-center justify-center py-20">
                    <Trophy className="h-16 w-16 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">{t("tournament.noTournaments")}</h3>
                    <p className="text-muted-foreground text-center">
                      {t("tournament.noTournamentsHint")}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {autoTournaments.map(tournament => (
                    <AutoTournamentCard key={tournament.id} tournament={tournament} />
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Regular Tournaments */}
            <TabsContent value="regular" className="mt-6">
              {regularTournaments.length === 0 ? (
                <Card className="glass border-border/50">
                  <CardContent className="flex flex-col items-center justify-center py-20">
                    <Trophy className="h-16 w-16 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Нет запланированных турниров</h3>
                    <p className="text-muted-foreground text-center mb-4">
                      Попробуйте 24/7 Arena турниры с ботами
                    </p>
                    <Button onClick={() => setActiveTab("arena")} className="btn-gold">
                      <Zap className="mr-2 h-4 w-4" />
                      Перейти в Арену
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {regularTournaments.map(tournament => (
                    <RegularTournamentCard key={tournament.id} tournament={tournament} />
                  ))}
                </div>
              )}
            </TabsContent>
          </>
        )}
      </Tabs>

      {/* Terms Notice */}
      <Card className="bg-muted/30 border-border/30">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-muted-foreground">
            <p>
              <strong className="text-foreground">{t("tournament.important")}</strong> {t("tournament.termsNotice")}{" "}
              <a href="/terms" className="text-primary hover:underline">
                {t("tournament.termsLink")}
              </a>.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
